/* eslint-disable @typescript-eslint/no-explicit-any */
import { sha512 } from "crypto-hash";
import { jwtDecode } from "jwt-decode";
import { getConfig } from "@/config/appConfig";

export const setCookie = (
  name: string,
  value: string,
  days: number = 30,
): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const isDevelopment = process.env.NODE_ENV === "development";
  const secureFlag = isDevelopment ? "" : ";Secure";

  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/${secureFlag};SameSite=Strict`;
};

export const getCookie = (name: string) => {
  const nameEQ = name + "=";
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) == " ") {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) == 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }

  return null;
};

export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

export const getHashedPassword = async (plainPassword: string) => {
  const salt = getConfig().VITE_APP_EMPLOYEE_SALT;
  if (typeof salt === "undefined") return;
  const hash1 = await sha512(plainPassword + salt);
  const hash2 = await sha512(hash1 + salt);
  const hash3 = await sha512(hash2 + salt);
  return hash3;
};

export const getHashedPasswordForNormal = async (plainPassword: string) => {
  const salt = getConfig().VITE_APP_EMPLOYEE_SALT;
  const hash1 = await sha512(plainPassword + salt);
  const hash2 = await sha512(hash1 + salt);
  const hash3 = await sha512(hash2 + salt);
  return hash3;
};

export const getUserFromToken: (token: string | any) => {
  FirstName: string;
  MiddleName: string;
  LastName: string;
  UserName: string;
  UserAccountID: string;
  is2FAEnabled: string;
  DateOfBirth: string;
  EmailAddress: string;
  EmployeeID: string;
  EmployerID: string;
  SootchyEmployerID: string;
  VestingScheduleName: string;
  EnrolledStatus: string;
  OnboardingProgress: string;
  IsDefaultPasswordChanged: string;
} = (token: string) => {
  try {
    const user = jwtDecode<any>(token);
    return user;
  } catch {
    return false;
  }
};

export const formatPhoneNumber = (input: string) => {
  if (input.startsWith("+1 ")) {
    return input.replace("+1 ", "1");
  } else {
    return `+1 ${input.slice(1)}`;
  }
};

export const formatVestingSchedule = (value: string) => {
  switch (value) {
    case "OneYear":
      return "1 year";
    case "TwoYear":
      return "2 years";
    case "ThreeYear":
      return "3 years";
    case "FourYear":
      return "4 years";
    case "FiveYear":
      return "5 years";
    case "Immediately":
      return "Immediately";

    default:
      return "Unknown";
  }
};

export const formatGoalDescription = (goal: string) => {
  switch (goal) {
    case "CollegeFund":
      return "Saving for college or university";

    case "TradeSchoolFund":
      return "Saving for trade school";

    default:
      return "";
  }
};

export const formatRelationshipKeys = (relationship: string) => {
  switch (relationship) {
    case "Child":
      return "My Child";
    case "Grandchild":
      return "My Grand child";
    case "NieceOrNephew":
      return "My niece/nephew";
    case "Other":
      return "Other";

    default:
      return "";
  }
};

export const formatMoney = (amount: number): string => {
  if (amount !== 0) {
    return amount.toLocaleString("en-US");
  }
  return "0";
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

export const formatDateForAPI = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const dateOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
};

export const calculateAge = (dateOfBirth: string) => {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

export const formatPlainTextToHtml = (text: string): string => {
  return text
    .split("\n\n")
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, "<br>")}</p>`)
    .join("");
};
